import { useEffect, useState } from 'react';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import { Loader } from 'components/Loader';
import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { TotemSelect } from 'components/TotemSelect';
import { NayaxMDBTemplateSelect } from 'components/Select/NayaxMDBTemplateSelect';
import { PermissionGate } from 'components/PermissionGate';
import {
    MicrostoreColumnTemplateState,
    useCreateMicrostoreColumnMutation,
    useGetMicrostoreColumnTemplatesQuery,
    TotadminPermission,
} from 'data/__generated__';

export function CreateMicrostoreColumnPopup({
    isOpen,
    setIsOpen,
    columnGroup,
}: {
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
    columnGroup: { _id: string; columns: { _id: string }[] };
}) {
    const [positionInColumnGroup, setPositionInColumnGroup] = useState<number>(columnGroup.columns.length);
    const [microstoreColumnTemplateId, setMicrostoreColumnTemplateId] = useState<string | null>(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

    useEffect(() => {
        setPositionInColumnGroup(columnGroup.columns.length);
    }, [setPositionInColumnGroup, columnGroup]);

    const {
        loading: microstoreColumnTemplateLoading,
        data: microstoreColumnTemplateData,
        error: microstoreColumnTemplateError,
    } = useGetMicrostoreColumnTemplatesQuery();

    const [createMicrostoreColumn, { loading: creationLoading }] = useCreateMicrostoreColumnMutation();

    if (microstoreColumnTemplateLoading) {
        return (
            <TotemPopup title="Ajouter une colonne" isOpen={isOpen} setIsOpen={setIsOpen} contentOverflow="visible">
                <Loader />
            </TotemPopup>
        );
    }

    if (microstoreColumnTemplateError || !microstoreColumnTemplateData) {
        throw new Error('Une erreur est survenue lors de la récupération des templates de colonne microstore');
    }

    async function handleSubmit() {
        if (!microstoreColumnTemplateId) {
            return;
        }

        const { data } = await createMicrostoreColumn({
            variables: {
                createMicrostoreColumnInput: {
                    columnGroupId: columnGroup._id,
                    positionInColumnGroup,
                    microstoreColumnTemplateId,
                    nayaxMDBTemplateId: selectedTemplateId,
                },
            },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors de la creation du template de colonne');
        }

        const {
            createMicrostoreColumnMutation: { errors },
        } = data;

        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });

            if (errors.length > 1) {
                toast.info('Cliquez pour fermer toutes les notifications', {
                    autoClose: false,
                    onClick: () => toast.dismiss(),
                });
            }
        }

        setIsOpen(false);
    }

    const { microstoreColumnTemplatesQuery: microstoreColumnTemplates } = microstoreColumnTemplateData;

    const microstoreColumnTemplateOptions =
        microstoreColumnTemplates
            .filter(
                (microstoreColumnTemplate) => microstoreColumnTemplate.state === MicrostoreColumnTemplateState.Regular,
            )
            .map(({ _id: microstoreColumnTemplateId, name }) => ({
                value: microstoreColumnTemplateId,
                label: name,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) || [];

    const positionInColumnGroupOptions = Array(columnGroup.columns.length + 1)
        .fill(undefined)
        .map((_, index) => ({
            value: index,
            label: `${index + 1}`,
        }));

    return (
        <TotemPopup title="Ajouter une colonne" isOpen={isOpen} setIsOpen={setIsOpen} contentOverflow="visible">
            <SubmitContainer>
                <TotemSelect<number>
                    label="Position de la colonne"
                    placeholder="Sélectionner la position de la colonne"
                    value={positionInColumnGroup}
                    options={positionInColumnGroupOptions}
                    onChange={(value) => {
                        if (value !== null) {
                            setPositionInColumnGroup(value);
                        }
                    }}
                />
                <TotemSelect
                    label="Template de colonne microstore"
                    placeholder="Sélectionner le template de colonnes"
                    value={microstoreColumnTemplateId}
                    options={microstoreColumnTemplateOptions}
                    onChange={(value) => {
                        if (value) {
                            setMicrostoreColumnTemplateId(value);
                        }
                    }}
                />
                <PermissionGate requiredPermissions={[TotadminPermission.NayaxTemplatesRead]}>
                    <NayaxMDBTemplateSelect value={selectedTemplateId} onChange={setSelectedTemplateId} />
                </PermissionGate>
                <TotemPrimaryLoadingButton
                    loading={creationLoading}
                    onClick={handleSubmit}
                    disabled={!microstoreColumnTemplateId}
                    useIcon={false}
                    label="Confirmer"
                    requiredPermissions={[TotadminPermission.MicrostoreColumnsManage]}
                />
            </SubmitContainer>
        </TotemPopup>
    );
}

const SubmitContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: 15px;

    > :not(:first-child) {
        margin-top: 15px;
    }
`;
